import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  getBeginnerExamCatalog, createBeginnerExamCatalog, updateBeginnerExamCatalog, deleteBeginnerExamCatalog,
  getBeginnerSections, createBeginnerSection, updateBeginnerSection, deleteBeginnerSection,
  getBeginnerPracticeSets, createBeginnerPracticeSet, updateBeginnerPracticeSet, deleteBeginnerPracticeSet,
  getBeginnerExamAccess, createBeginnerExamAccess, updateBeginnerExamAccess, deleteBeginnerExamAccess,
  getBeginnerSubscriptions, updateBeginnerSubscription, uploadBeginnerQuestionPDF,
  getBeginnerQuestionDocuments,
  deleteBeginnerQuestionDocument,
  setBeginnerQuestionDocumentActive,
  getBeginnerQuestions, updateBeginnerQuestion,
} from "../api/beginnerAdminClient";
import "../styles/BeginnerAdmin.css";
import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";
import BeginnerSubscriptionPlanManager from "./BeginnerSubscriptionPlanManager";

const TABS = [
  ["overview", "Overview"],
  ["catalog", "Create Main Exam"],
  ["sections", "Practice Sections"],
  ["practice", "Practice Sets"],
  ["exams", "Main Exam Access"],
  ["question-bank", "Question Bank"],
  ["subscriptions", "Subscriptions"],
  ["documents", "PDF Documents"],
  ["subscription-plans", "Subscription Plans"],
  ["results", "Beginner Results"],
];

const PDF_SECTIONS = [
  ["MAIN", "Main Exams"],
  ["PRACTICE", "Practice"],
];

const rows = (r) => {
  const d = r?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.results)) return d.results;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const errorText = (e) => {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d && typeof d === "object") {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : v}`)
      .join(" ");
  }
  return e?.message || "Request failed.";
};

const toNumber = (value) => Number(value) || 0;

export default function BeginnerAdmin() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const validTab = TABS.some(([id]) => id === requested) ? requested : "overview";
  const [tab, setTab] = useState(validTab);

  const [catalog, setCatalog] = useState([]);
  const [sections, setSections] = useState([]);
  const [practiceSets, setPracticeSets] = useState([]);
  const [examAccess, setExamAccess] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  const [questionBank, setQuestionBank] = useState([]);
  const [questionFilter, setQuestionFilter] = useState("");
  const [questionTypeFilter, setQuestionTypeFilter] = useState("ALL");
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Messages/errors are tied to the tab that created them, so they don't leak across tabs.
  const [errorState, setErrorState] = useState("");
  const [messageState, setMessageState] = useState("");
  const [errorTab, setErrorTab] = useState(validTab);
  const [messageTab, setMessageTab] = useState(validTab);
  const activeTabRef = useRef(tab);
  activeTabRef.current = tab;
  const setError = useCallback((value) => {
    setErrorState(value);
    setErrorTab(activeTabRef.current);
  }, []);
  const setMessage = useCallback((value) => {
    setMessageState(value);
    setMessageTab(activeTabRef.current);
  }, []);
  const error = errorTab === tab ? errorState : "";
  const message = messageTab === tab ? messageState : "";

  const [editingCatalog, setEditingCatalog] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingPractice, setEditingPractice] = useState(null);
  const [editingAccess, setEditingAccess] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);

  // PDF import state. The import target is derived from the active tab.
  const uploadTarget = tab === "practice" ? "PRACTICE" : "MAIN";
  const [uploadId, setUploadId] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [pdfConfigs, setPdfConfigs] = useState([]);
  const [uploadTitle, setUploadTitle] = useState("");
  const [importResults, setImportResults] = useState(null);

  // PDF Documents tab state.
  const [pdfDocuments, setPdfDocuments] = useState([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [deletingPdfs, setDeletingPdfs] = useState(false);
  const [pdfSection, setPdfSection] = useState("MAIN");

  // State for viewing PDF document questions modal
  const [viewingPdfDocument, setViewingPdfDocument] = useState(null);

  /* ------------------------------ tab handling ------------------------------ */

  useEffect(() => {
    setTab(validTab);
    if (!requested || !TABS.some(([id]) => id === requested)) {
      setParams({ tab: "overview" }, { replace: true });
    }
  }, [validTab, requested, setParams]);

  // Clear the shared upload form whenever the tab changes (practice <-> exams).
  useEffect(() => {
    setUploadId("");
    setUploadFiles([]);
    setPdfConfigs([]);
    setUploadTitle("");
    setImportResults(null);
  }, [tab]);

  /* ------------------------------- data loading ------------------------------ */

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    const calls = await Promise.allSettled([
      getBeginnerExamCatalog(),
      getBeginnerSections(),
      getBeginnerPracticeSets(),
      getBeginnerExamAccess(),
      getBeginnerSubscriptions(),
    ]);
    const setters = [setCatalog, setSections, setPracticeSets, setExamAccess, setSubscriptions];
    const names = [
      "Main exam catalog",
      "Practice sections",
      "Beginner practice sets",
      "Beginner main exam access",
      "Beginner subscriptions",
    ];
    const failed = [];
    calls.forEach((result, i) => {
      if (result.status === "fulfilled") setters[i](rows(result.value));
      else failed.push(`${names[i]}: ${errorText(result.reason)}`);
    });
    if (failed.length) setError(`Some Beginner data could not load. ${failed.join(" | ")}`);
    setLoading(false);
  }, [setError]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const loadPdfDocuments = useCallback(async () => {
    setLoadingPdfs(true);
    setError("");
    try {
      const response = await getBeginnerQuestionDocuments();
      setPdfDocuments(rows(response));
    } catch (e) {
      setError(`Uploaded PDFs: ${errorText(e)}`);
    } finally {
      setLoadingPdfs(false);
    }
  }, [setError]);

  useEffect(() => {
    if (tab === "documents" || tab === "question-bank") loadPdfDocuments();
  }, [tab, loadPdfDocuments]);

  const loadQuestionBank = useCallback(
    async (accessId = questionFilter) => {
      setLoadingQuestions(true);
      try {
        const response = await getBeginnerQuestions(accessId);
        setQuestionBank(rows(response));
      } catch (e) {
        setError(`Question bank: ${errorText(e)}`);
      } finally {
        setLoadingQuestions(false);
      }
    },
    [questionFilter, setError]
  );

  useEffect(() => {
    if (tab === "question-bank") loadQuestionBank(questionFilter);
  }, [tab, questionFilter, loadQuestionBank]);

  /* --------------------------- PDF document actions --------------------------- */

  const deleteOnePdf = async (document) => {
    const confirmed = window.confirm(
      `Delete "${document.title}" and all questions imported from this PDF? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingPdfs(true);
    setError("");
    setMessage("");
    try {
      await deleteBeginnerQuestionDocument(document.id);
      setPdfDocuments((previous) => previous.filter((item) => item.id !== document.id));
      setMessage(`"${document.title}" was deleted.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setDeletingPdfs(false);
    }
  };

  const togglePdfActive = async (document) => {
    const nextActive = !document.is_active;
    if (
      !window.confirm(
        `${nextActive ? "Activate" : "Deactivate"} "${document.title}"? This affects only this PDF.`
      )
    ) {
      return;
    }
    setDeletingPdfs(true);
    setError("");
    setMessage("");
    try {
      await setBeginnerQuestionDocumentActive(document.id, nextActive);
      setPdfDocuments((previous) =>
        previous.map((item) => (item.id === document.id ? { ...item, is_active: nextActive } : item))
      );
      setMessage(`"${document.title}" ${nextActive ? "activated" : "deactivated"}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setDeletingPdfs(false);
    }
  };

  const pdfCounts = useMemo(
    () => ({
      MAIN: pdfDocuments.filter((d) => d.target_type === "MAIN").length,
      PRACTICE: pdfDocuments.filter((d) => d.target_type === "PRACTICE").length,
    }),
    [pdfDocuments]
  );

  const visiblePdfDocuments = useMemo(
    () => pdfDocuments.filter((d) => d.target_type === pdfSection),
    [pdfDocuments, pdfSection]
  );

  /* ------------------------------ question marks ------------------------------ */

  const saveQuestionMarks = async (event, question) => {
    event.preventDefault();
    const marks = Number(new FormData(event.currentTarget).get("marks"));
    if (!Number.isFinite(marks) || marks < 0) {
      setError("Marks must be a valid number greater than or equal to zero.");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await updateBeginnerQuestion(question.id, { marks });
      const updated = response?.data || { ...question, marks };
      setQuestionBank((previous) =>
        previous.map((item) => (item.id === question.id ? { ...item, ...updated } : item))
      );
      setMessage(`Marks updated for Question ${question.question_number}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------------- stats ---------------------------------- */

  const stats = useMemo(
    () => ({
      catalog: catalog.length,
      sections: sections.length,
      practice: practiceSets.length,
      published: practiceSets.filter((x) => x.is_published).length,
      main: examAccess.length,
      subscriptions: subscriptions.filter((x) => x.status === "ACTIVE").length,
    }),
    [catalog, sections, practiceSets, examAccess, subscriptions]
  );

  /* ------------------------------ generic save/remove ------------------------------ */

  const runSave = async (event, editing, createFn, updateFn, payloadFn, reset, success) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const payload = payloadFn(new FormData(event.currentTarget));
      if (
        payload.minimum_class != null &&
        payload.maximum_class != null &&
        Number(payload.minimum_class) > Number(payload.maximum_class)
      ) {
        throw new Error("Minimum class cannot be greater than maximum class.");
      }
      if (editing?.id) await updateFn(editing.id, payload);
      else await createFn(payload);
      reset(null);
      setMessage(success);
      await loadAll();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (label, fn) => {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await fn();
      setMessage(`${label} deleted.`);
      await loadAll();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const saveCatalog = (e) =>
    runSave(
      e,
      editingCatalog,
      createBeginnerExamCatalog,
      updateBeginnerExamCatalog,
      (f) => ({
        name: String(f.get("name") || "").trim(),
        class_level: Number(f.get("class_level")),
        fee: Number(f.get("fee") || 0),
        duration_minutes: Number(f.get("duration_minutes") || 60),
        is_active: f.get("is_active") === "on",
      }),
      setEditingCatalog,
      "Main exam saved."
    );

  const saveSection = (e) =>
    runSave(
      e,
      editingSection,
      createBeginnerSection,
      updateBeginnerSection,
      (f) => ({
        exam: Number(f.get("exam")),
        name: String(f.get("name") || "").trim(),
        duration_minutes: Number(f.get("duration_minutes") || 0),
        question_count: Number(f.get("question_count") || 0),
        is_active: f.get("is_active") === "on",
      }),
      setEditingSection,
      "Practice section saved."
    );

  const savePractice = (e) =>
    runSave(
      e,
      editingPractice,
      createBeginnerPracticeSet,
      updateBeginnerPracticeSet,
      (f) => ({
        title: String(f.get("title") || "").trim(),
        section: Number(f.get("section")),
        minimum_class: Number(f.get("minimum_class")),
        maximum_class: Number(f.get("maximum_class")),
        question_count: Number(f.get("question_count")),
        duration_minutes: Number(f.get("duration_minutes")),
        is_published: f.get("is_published") === "on",
      }),
      setEditingPractice,
      "Beginner practice set saved."
    );

  const saveAccess = (e) =>
    runSave(
      e,
      editingAccess,
      createBeginnerExamAccess,
      updateBeginnerExamAccess,
      (f) => ({
        exam: Number(f.get("exam")),
        minimum_class: Number(f.get("minimum_class")),
        maximum_class: Number(f.get("maximum_class")),
        mcq_question_count: null,
        essay_question_count: null,
        is_published: f.get("is_published") === "on",
        subscription_required: f.get("subscription_required") === "on",
      }),
      setEditingAccess,
      "Beginner main exam access saved."
    );

  const saveSubscription = async (e) => {
    e.preventDefault();
    if (!editingSubscription) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await updateBeginnerSubscription(editingSubscription.id, {
        status: f.get("status"),
        admin_notes: f.get("admin_notes") || "",
      });
      setEditingSubscription(null);
      setMessage("Subscription updated.");
      await loadAll();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------- PDF upload ------------------------------- */

  const uploadPDFs = async (e) => {
    e.preventDefault();
    const formElement = e.currentTarget; // keep reference before the first await

    setBusy(true);
    setError("");
    setMessage("");
    setImportResults(null);

    try {
      const form = new FormData();
      form.append("target_type", uploadTarget);
      form.append("target_id", uploadId);
      if (uploadTitle.trim()) form.append("title", uploadTitle.trim());
      uploadFiles.forEach((file) => form.append("pdf_files", file));
      // The upload endpoint must persist these per-file settings.
      form.append("pdf_configs", JSON.stringify(pdfConfigs));

      const response = await uploadBeginnerQuestionPDF(form);

      setImportResults(response.data);
      setMessage(`PDF import finished: ${response.data?.questions_created ?? 0} questions created.`);
      setUploadFiles([]);
      setPdfConfigs([]);
      setUploadTitle("");
      if (formElement) formElement.reset();
      await loadPdfDocuments();
    } catch (err) {
      setError(errorText(err));
      if (err?.response?.data) setImportResults(err.response.data);
    } finally {
      setBusy(false);
    }
  };

  const pdfConfigInvalid = pdfConfigs.some(
    (item) =>
      !item.question_count ||
      Number(item.question_count) < 1 ||
      !item.marks_per_question ||
      Number(item.marks_per_question) <= 0
  );

  /* ------------------------------ small UI helpers ------------------------------ */

  const input = (label, name, value, type = "text", extra = {}) => (
    <label key={name}>
      {label}
      <input name={name} type={type} defaultValue={value ?? ""} {...extra} />
    </label>
  );

  const classSelect = (label, name, value, extra = {}) => (
    <label key={name}>
      {label}
      <select name={name} defaultValue={value ?? ""} required {...extra}>
        <option value="">Choose class</option>
        {Array.from({ length: 8 }, (_, index) => index + 5).map((classNumber) => (
          <option key={classNumber} value={classNumber}>
            Class {classNumber}
          </option>
        ))}
      </select>
    </label>
  );

  const check = (name, checked, label) => (
    <label className="beginner-admin-check" key={name}>
      <input name={name} type="checkbox" defaultChecked={Boolean(checked)} /> {label}
    </label>
  );

  const actions = (editing, setEditing, label) => (
    <div className="beginner-admin-actions">
      <button disabled={busy}>{busy ? "Saving…" : editing ? "Save changes" : label}</button>
      {editing && (
        <button type="button" className="secondary" onClick={() => setEditing(null)}>
          Cancel
        </button>
      )}
    </div>
  );

  const table = (headers, body) => (
    <div className="beginner-admin-table-wrap">
      <table>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );

  const examNameOf = (x) =>
    x.exam_name || catalog.find((c) => c.id === Number(x.exam))?.name || x.exam;

  // Shared PDF import panel used by both "Practice Sets" and "Main Exam Access" tabs.
  const renderPdfImportPanel = (target) => {
    const isMain = target === "MAIN";
    const targets = isMain ? examAccess : practiceSets;
    return (
      <section
        className={`beginner-admin-panel ${
          isMain ? "beginner-main-exam-pdf-panel" : "beginner-practice-pdf-panel"
        }`}
      >
        <h2>{isMain ? "Main Exam PDFs and Mark Distribution" : "Practice Set PDFs and Mark Distribution"}</h2>
        <p className="beginner-admin-note">
          Each PDF has its own question count and marks per question; no shared marks setting is used.
          {isMain
            ? " Create the Main Exam Access record first, then select it below."
            : " Create the Practice Set first, then select it below."}
        </p>
        <form className="beginner-admin-form" onSubmit={uploadPDFs}>
          <label>
            {isMain ? "Main Exam Access record" : "Practice Set"}
            <select value={uploadId} onChange={(e) => setUploadId(e.target.value)} required>
              <option value="">{isMain ? "Choose main exam access" : "Choose Practice Set"}</option>
              {targets.map((x) => (
                <option key={x.id} value={x.id}>
                  {isMain
                    ? examNameOf(x) || `Exam ${x.id}`
                    : `${x.title} — ${
                        x.section_name ||
                        sections.find((s) => s.id === Number(x.section))?.name ||
                        "Practice Section"
                      }`}
                </option>
              ))}
            </select>
          </label>
          <label>
            Optional document title
            <input
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              maxLength={255}
              placeholder="Defaults to each PDF filename"
            />
          </label>
          <label>
            Question PDF files
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              required
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setUploadFiles(files);
                setPdfConfigs(
                  files.map((file) => ({ file_name: file.name, question_count: "", marks_per_question: "1" }))
                );
              }}
            />
          </label>

          {pdfConfigs.length > 0 && (
            <div className="beginner-pdf-config-list">
              <h3>Configure each PDF</h3>
              {pdfConfigs.map((config, index) => (
                <div className="beginner-pdf-config-row" key={`${config.file_name}-${index}`}>
                  <strong>{config.file_name}</strong>
                  <label>
                    Number of questions
                    <input
                      type="number"
                      min="1"
                      required
                      value={config.question_count}
                      onChange={(e) =>
                        setPdfConfigs((prev) =>
                          prev.map((item, i) => (i === index ? { ...item, question_count: e.target.value } : item))
                        )
                      }
                    />
                  </label>
                  <label>
                    Marks per question
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      value={config.marks_per_question}
                      onChange={(e) =>
                        setPdfConfigs((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, marks_per_question: e.target.value } : item
                          )
                        )
                      }
                    />
                  </label>
                  <p className="beginner-pdf-row-total">
                    PDF total: {(toNumber(config.question_count) * toNumber(config.marks_per_question)).toFixed(2)} marks
                  </p>
                </div>
              ))}
              <p className="beginner-admin-note">
                Total configured questions: {pdfConfigs.reduce((sum, item) => sum + Math.max(0, toNumber(item.question_count)), 0)}{" "}
                · Total marks:{" "}
                {pdfConfigs
                  .reduce(
                    (sum, item) =>
                      sum + Math.max(0, toNumber(item.question_count)) * Math.max(0, toNumber(item.marks_per_question)),
                    0
                  )
                  .toFixed(2)}
              </p>
            </div>
          )}

          <div className="beginner-admin-actions">
            <button disabled={busy || !uploadId || !uploadFiles.length || pdfConfigInvalid}>
              {busy ? "Importing PDFs…" : "Import PDFs and their questions"}
            </button>
          </div>
        </form>

        {importResults && (
          <div className="beginner-admin-note beginner-pdf-import-results">
            <strong>Last import:</strong> {importResults.questions_created ?? 0} questions created ·{" "}
            {importResults.failed_files ?? 0} failed files.
            {Array.isArray(importResults.results) &&
              importResults.results.map((item, index) => (
                <p key={`${item.document_id || item.file || index}`}>
                  <strong>{item.file || `PDF ${index + 1}`}</strong>: {item.status || "Processed"} ·{" "}
                  {item.questions_created ?? 0} imported / {item.configured_question_count ?? "—"} configured
                  {item.count_matches_configuration === false ? " · Question count mismatch" : ""}
                  {item.error ? ` · ${item.error}` : ""}
                </p>
              ))}
          </div>
        )}
      </section>
    );
  };

  /* ---------------------------------- render ---------------------------------- */

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <AdminTopbar />
        <main className="beginner-admin-page">
          <div className="beginner-admin-heading">
            <div>
              <div className="beginner-admin-eyebrow">ADMINISTRATION</div>
              <h1>Beginner Management</h1>
              <p>
                Create main exams, organize practice sections and sets, configure question PDFs, access and
                subscriptions.
              </p>
            </div>
            <Link className="beginner-admin-back" to="/admin/dashboard">
              ← Admin Dashboard
            </Link>
          </div>

          <nav className="beginner-admin-tabs" aria-label="Beginner management sections">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={tab === id ? "active" : ""}
                onClick={() => {
                  setTab(id);
                  setParams({ tab: id });
                }}
              >
                {label}
              </button>
            ))}
          </nav>

          {message && (
            <div className="beginner-admin-alert success" role="status">
              {message}
            </div>
          )}
          {error && (
            <div className="beginner-admin-alert error" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <div className="beginner-admin-loading">Loading Beginner configuration…</div>
          ) : (
            <>
              {/* ------------------------------ OVERVIEW ------------------------------ */}
              {tab === "overview" && (
                <>
                  <section className="beginner-admin-stats">
                    {[
                      ["Exam catalog", stats.catalog],
                      ["Sections", stats.sections],
                      ["Practice sets", stats.practice],
                      ["Published sets", stats.published],
                      ["Main access records", stats.main],
                      ["Active subscriptions", stats.subscriptions],
                    ].map(([label, value]) => (
                      <article className="beginner-admin-stat" key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </article>
                    ))}
                  </section>
                  <section className="beginner-admin-panel">
                    <h2>Setup order</h2>
                    <ol>
                      <li>Create a main exam and configure its PDFs.</li>
                      <li>Create Practice Sections for organizing practice content.</li>
                      <li>Create multiple practice sets under a selected Practice Section and assign eligible classes.</li>
                      <li>Review uploaded PDFs and imported questions in PDF Documents and Question Bank.</li>
                    </ol>
                    <p className="beginner-admin-note">
                      This page uses Beginner-specific API endpoints. It does not use the Professional exam catalog or
                      professional sections. Assign each catalog exam and subscription plan to a class. Practice Set and
                      Main Exam Access records currently use minimum/maximum class ranges; set both ends to the same class
                      when access must be limited to one class.
                    </p>
                  </section>
                </>
              )}

              {/* ------------------------------ CATALOG ------------------------------ */}
              {tab === "catalog" && (
                <div className="beginner-admin-columns">
                  <section className="beginner-admin-panel">
                    <h2>{editingCatalog ? "Edit Main Exam" : "Create Main Exam"}</h2>
                    <form className="beginner-admin-form" onSubmit={saveCatalog} key={editingCatalog?.id || "catalog-new"}>
                      {input("Exam name", "name", editingCatalog?.name, "text", {
                        required: true,
                        maxLength: 180,
                        placeholder: "e.g. Beginner Mathematics",
                      })}
                      <div className="beginner-admin-form-grid">
                        {classSelect("Assigned class", "class_level", editingCatalog?.class_level ?? "")}
                        {input("Fee", "fee", editingCatalog?.fee ?? 0, "number", { min: 0, step: "0.01", required: true })}
                        {input("Duration (minutes)", "duration_minutes", editingCatalog?.duration_minutes ?? 60, "number", {
                          min: 1,
                          max: 600,
                          required: true,
                        })}
                      </div>
                      <p className="beginner-admin-note">
                        Assign this exam to one class. Students should only see exams assigned to their registered class.
                      </p>
                      {check("is_active", editingCatalog?.is_active !== false, "Active")}
                      {actions(editingCatalog, setEditingCatalog, "Create Main Exam")}
                    </form>
                  </section>
                  <section className="beginner-admin-panel">
                    <h2>Main Exam Catalog ({catalog.length})</h2>
                    {catalog.length === 0 ? (
                      <p className="beginner-admin-empty">No main exams yet. Create a main exam first.</p>
                    ) : (
                      table(
                        ["Exam", "Class", "Fee", "Duration", "Status", "Actions"],
                        catalog.map((x) => (
                          <tr key={x.id}>
                            <td>{x.name}</td>
                            <td>{x.class_level ? `Class ${x.class_level}` : "Not assigned"}</td>
                            <td>{x.fee ?? "—"}</td>
                            <td>{x.duration_minutes ?? "—"} min</td>
                            <td>{x.is_active ? "Active" : "Inactive"}</td>
                            <td className="beginner-admin-row-actions">
                              <button type="button" onClick={() => setEditingCatalog(x)}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className="danger"
                                disabled={busy}
                                onClick={() => remove("Beginner exam", () => deleteBeginnerExamCatalog(x.id))}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )
                    )}
                  </section>
                </div>
              )}

              {/* ------------------------------ SECTIONS ------------------------------ */}
              {tab === "sections" && (
                <div className="beginner-admin-columns">
                  <section className="beginner-admin-panel">
                    <h2>{editingSection ? "Edit practice section" : "Create Practice Section"}</h2>
                    <form className="beginner-admin-form" onSubmit={saveSection} key={editingSection?.id || "section-new"}>
                      <label>
                        Main exam
                        <select name="exam" required defaultValue={editingSection?.exam || ""}>
                          <option value="">Choose exam</option>
                          {catalog.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      {input("Section name", "name", editingSection?.name, "text", {
                        required: true,
                        maxLength: 180,
                        placeholder: "e.g. Algebra",
                      })}
                      {input("Duration (minutes)", "duration_minutes", editingSection?.duration_minutes ?? 30, "number", {
                        min: 0,
                        max: 600,
                        required: true,
                      })}
                      {input("Question count", "question_count", editingSection?.question_count ?? 10, "number", {
                        min: 0,
                        max: 500,
                        required: true,
                      })}
                      {check("is_active", editingSection?.is_active !== false, "Active")}
                      {actions(editingSection, setEditingSection, "Create Practice Section")}
                      {catalog.length === 0 && <p className="beginner-admin-note">Create a main exam first.</p>}
                    </form>
                  </section>
                  <section className="beginner-admin-panel">
                    <h2>Practice Sections ({sections.length})</h2>
                    {sections.length === 0 ? (
                      <p className="beginner-admin-empty">No practice sections yet.</p>
                    ) : (
                      table(
                        ["Section", "Exam", "Duration", "Questions", "Status", "Actions"],
                        sections.map((x) => (
                          <tr key={x.id}>
                            <td>{x.name}</td>
                            <td>{examNameOf(x)}</td>
                            <td>{x.duration_minutes ?? "—"} min</td>
                            <td>{x.question_count ?? "—"}</td>
                            <td>{x.is_active === false ? "Inactive" : "Active"}</td>
                            <td className="beginner-admin-row-actions">
                              <button type="button" onClick={() => setEditingSection(x)}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className="danger"
                                disabled={busy}
                                onClick={() => remove("Beginner section", () => deleteBeginnerSection(x.id))}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )
                    )}
                  </section>
                </div>
              )}

              {/* ------------------------------ PRACTICE ------------------------------ */}
              {tab === "practice" && (
                <>
                  <div className="beginner-admin-columns">
                    <section className="beginner-admin-panel">
                      <h2>{editingPractice ? "Edit practice set" : "Create practice set"}</h2>
                      <form className="beginner-admin-form" onSubmit={savePractice} key={editingPractice?.id || "practice-new"}>
                        {input("Title", "title", editingPractice?.title, "text", { required: true, maxLength: 180 })}
                        <label>
                          Choose Practice Section
                          <select name="section" required defaultValue={editingPractice?.section || ""}>
                            <option value="">Choose Practice Section</option>
                            {sections.map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.name} — {examNameOf(x) || "Main exam"}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="beginner-admin-form-grid">
                          {classSelect("Available from class", "minimum_class", editingPractice?.minimum_class ?? "")}
                          {classSelect("Available through class", "maximum_class", editingPractice?.maximum_class ?? "")}
                          {input("Question count", "question_count", editingPractice?.question_count ?? 10, "number", {
                            min: 1,
                            max: 500,
                            required: true,
                          })}
                          {input("Duration (minutes)", "duration_minutes", editingPractice?.duration_minutes ?? 15, "number", {
                            min: 1,
                            max: 600,
                            required: true,
                          })}
                        </div>
                        <p className="beginner-admin-note">
                          Choose the eligible class range. To make this Practice Set available only to Class 8 students,
                          select Class 8 for both fields. Students should see it only when the class on their
                          registration/profile falls within this range.
                        </p>
                        {check("is_published", editingPractice?.is_published, "Published for students")}
                        <p className="beginner-admin-note">
                          Practice sets should be accessible only to students with an active eligible subscription. This
                          must also be enforced by the backend API.
                        </p>
                        {actions(editingPractice, setEditingPractice, "Create practice set")}
                      </form>
                    </section>
                    <section className="beginner-admin-panel">
                      <h2>Practice sets ({practiceSets.length})</h2>
                      {practiceSets.length === 0 ? (
                        <p className="beginner-admin-empty">No practice sets yet.</p>
                      ) : (
                        table(
                          ["Title", "Section", "Classes", "Questions", "Status", "Actions"],
                          practiceSets.map((x) => (
                            <tr key={x.id}>
                              <td>{x.title}</td>
                              <td>{x.section_name || sections.find((s) => s.id === Number(x.section))?.name || x.section}</td>
                              <td>
                                {x.minimum_class}–{x.maximum_class}
                              </td>
                              <td>{x.question_count}</td>
                              <td>{x.is_published ? "Published" : "Draft"}</td>
                              <td className="beginner-admin-row-actions">
                                <button type="button" onClick={() => setEditingPractice(x)}>
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="danger"
                                  disabled={busy}
                                  onClick={() => remove("practice set", () => deleteBeginnerPracticeSet(x.id))}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )
                      )}
                    </section>
                  </div>
                  {renderPdfImportPanel("PRACTICE")}
                </>
              )}

              {/* ------------------------------ MAIN EXAM ACCESS ------------------------------ */}
              {tab === "exams" && (
                <>
                  <div className="beginner-admin-columns">
                    <section className="beginner-admin-panel">
                      <h2>{editingAccess ? "Edit main exam access" : "Configure main exam access"}</h2>
                      <form className="beginner-admin-form" onSubmit={saveAccess} key={editingAccess?.id || "access-new"}>
                        <label>
                          Main exam
                          <select name="exam" required defaultValue={editingAccess?.exam || ""}>
                            <option value="">Choose main exam</option>
                            {catalog.map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="beginner-admin-form-grid">
                          {classSelect("Available from class", "minimum_class", editingAccess?.minimum_class ?? "")}
                          {classSelect("Available through class", "maximum_class", editingAccess?.maximum_class ?? "")}
                        </div>
                        <p className="beginner-admin-note">
                          Question counts and marks are configured separately for each PDF below. The exam attempt uses
                          the questions imported into its selected exam bank.
                        </p>
                        <p className="beginner-admin-note">
                          Class access: choose a range from Class 5 to Class 12. To target one class only, choose the same
                          class in both fields. The student must be registered under a class inside this range.
                        </p>
                        {check("is_published", editingAccess?.is_published, "Published for Beginner students")}
                        {check("subscription_required", editingAccess?.subscription_required !== false, "Subscription required")}
                        {actions(editingAccess, setEditingAccess, "Add main exam access")}
                      </form>
                    </section>
                    <section className="beginner-admin-panel">
                      <h2>Main exam access ({examAccess.length})</h2>
                      {examAccess.length === 0 ? (
                        <p className="beginner-admin-empty">No main exam access records yet.</p>
                      ) : (
                        table(
                          ["Exam", "Classes", "MCQs", "Essays", "Subscription", "Status", "Actions"],
                          examAccess.map((x) => (
                            <tr key={x.id}>
                              <td>{examNameOf(x)}</td>
                              <td>
                                {x.minimum_class}–{x.maximum_class}
                              </td>
                              <td>{x.mcq_question_count == null ? "All" : x.mcq_question_count}</td>
                              <td>{x.essay_question_count == null ? "All" : x.essay_question_count}</td>
                              <td>{x.subscription_required ? "Required" : "Not required"}</td>
                              <td>{x.is_published ? "Published" : "Draft"}</td>
                              <td className="beginner-admin-row-actions">
                                <button type="button" onClick={() => setEditingAccess(x)}>
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="danger"
                                  disabled={busy}
                                  onClick={() => remove("main exam access", () => deleteBeginnerExamAccess(x.id))}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )
                      )}
                    </section>
                  </div>
                  {renderPdfImportPanel("MAIN")}
                </>
              )}

              {/* ------------------------------ QUESTION BANK ------------------------------ */}
              {tab === "question-bank" && (
                <section className="beginner-admin-panel beginner-question-bank-page">
                  <div className="beginner-admin-panel-title">
                    <div>
                      <h2>Question Bank</h2>
                      <p>
                        Manage exam PDFs, review imported questions, and configure marks without changing unrelated
                        examination settings.
                      </p>
                    </div>
                    <button type="button" className="secondary" onClick={() => setParams({ tab: "exams" })}>
                      Back to Exam Access
                    </button>
                  </div>

                  <div
                    className="beginner-admin-form-grid beginner-question-bank-filters"
                    style={{ maxWidth: "760px", marginBottom: "18px" }}
                  >
                    <label>
                      Choose main exam
                      <select value={questionFilter} onChange={(e) => setQuestionFilter(e.target.value)}>
                        <option value="">All main exams</option>
                        {examAccess.map((x) => (
                          <option key={x.id} value={x.id}>
                            {examNameOf(x) || `Exam ${x.id}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Question type
                      <select value={questionTypeFilter} onChange={(e) => setQuestionTypeFilter(e.target.value)}>
                        <option value="ALL">MCQ and Essay</option>
                        <option value="MCQ">MCQ</option>
                        <option value="ESSAY">Essay</option>
                      </select>
                    </label>
                  </div>

                  {(() => {
                    const selectedAccess = examAccess.find((x) => String(x.id) === String(questionFilter));
                    const selectedExamName = selectedAccess ? examNameOf(selectedAccess) : "All Main Exams";
                    const selectedDocs = pdfDocuments.filter(
                      (doc) =>
                        doc.target_type === "MAIN" &&
                        (!questionFilter || String(doc.target_id ?? doc.main_exam_access_id) === String(questionFilter))
                    );
                    const visibleQuestions = questionBank.filter(
                      (q) =>
                        questionTypeFilter === "ALL" ||
                        String(q.question_type || "").toUpperCase().includes(questionTypeFilter)
                    );
                    const configuredQuestions = selectedDocs.reduce(
                      (sum, doc) => sum + Number(doc.configured_question_count || 0),
                      0
                    );
                    const importedQuestions = selectedDocs.reduce(
                      (sum, doc) => sum + Number(doc.actual_questions ?? doc.questions_created ?? 0),
                      0
                    );
                    return (
                      <>
                        <div className="beginner-question-bank-summary">
                          <div>
                            <span>Selected exam</span>
                            <strong>{selectedExamName}</strong>
                          </div>
                          <div>
                            <span>Access status</span>
                            <strong>
                              {selectedAccess ? (selectedAccess.is_published ? "Published" : "Draft") : "All exams"}
                            </strong>
                          </div>
                          <div>
                            <span>PDF documents</span>
                            <strong>{selectedDocs.length}</strong>
                          </div>
                          <div>
                            <span>Configured / imported</span>
                            <strong>
                              {configuredQuestions} / {importedQuestions}
                            </strong>
                          </div>
                        </div>

                        <section className="beginner-question-bank-pdf-section">
                          <div className="beginner-admin-panel-title">
                            <div>
                              <h3>PDF Management</h3>
                              <p>Question count and marks are configured separately for each PDF during import.</p>
                            </div>
                            <button type="button" onClick={() => setParams({ tab: "exams" })}>
                              Upload PDF
                            </button>
                          </div>
                          {selectedDocs.length === 0 ? (
                            <p className="beginner-admin-empty">
                              No main-exam PDFs found for this selection. Upload a PDF to configure its question count and
                              marks per question.
                            </p>
                          ) : (
                            table(
                              ["PDF name", "Status", "Configured questions", "Imported questions", "Marks / question", "Total marks", "Count check"],
                              selectedDocs.map((doc) => (
                                <tr key={doc.id}>
                                  <td>
                                    <strong>{doc.title || doc.file_name}</strong>
                                    <div>{doc.file_name}</div>
                                  </td>
                                  <td>{doc.status || "—"}</td>
                                  <td>{doc.configured_question_count ?? "—"}</td>
                                  <td>{doc.actual_questions ?? doc.questions_created ?? 0}</td>
                                  <td>{doc.marks_per_question ?? "—"}</td>
                                  <td>{doc.total_configured_marks ?? "—"}</td>
                                  <td>
                                    {doc.count_matches_configuration === false
                                      ? "Mismatch"
                                      : doc.count_matches_configuration === true
                                      ? "Matches"
                                      : "—"}
                                  </td>
                                </tr>
                              ))
                            )
                          )}
                        </section>

                        <div className="beginner-question-bank-question-section">
                          <div className="beginner-admin-panel-title">
                            <div>
                              <h3>{questionTypeFilter === "ALL" ? "MCQ / Essay Questions" : `${questionTypeFilter} Questions`}</h3>
                              <p>Update marks per question. Main exam answers remain hidden from students.</p>
                            </div>
                            <span className="beginner-question-count">{visibleQuestions.length} questions</span>
                          </div>
                          {loadingQuestions ? (
                            <p className="beginner-admin-loading">Loading questions…</p>
                          ) : visibleQuestions.length === 0 ? (
                            <p className="beginner-admin-empty">
                              No questions found for this filter. Import a PDF under Main Exam Access first.
                            </p>
                          ) : (
                            table(
                              ["Exam", "Question", "Type", "Question text", "Marks", "Save"],
                              visibleQuestions.map((q) => (
                                <tr key={q.id}>
                                  <td>{q.exam_name || "—"}</td>
                                  <td>{q.question_number}</td>
                                  <td>{q.question_type}</td>
                                  <td style={{ minWidth: "280px", maxWidth: "520px", whiteSpace: "normal" }}>
                                    {q.question_text}
                                  </td>
                                  <td>
                                    <form id={`marks-${q.id}`} onSubmit={(e) => saveQuestionMarks(e, q)}>
                                      <input
                                        aria-label={`Marks for question ${q.question_number}`}
                                        name="marks"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        defaultValue={q.marks}
                                        required
                                        style={{ width: "90px" }}
                                      />
                                    </form>
                                  </td>
                                  <td>
                                    <button type="submit" form={`marks-${q.id}`} disabled={busy}>
                                      Save marks
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )
                          )}
                        </div>
                      </>
                    );
                  })()}
                </section>
              )}

              {/* ------------------------------ SUBSCRIPTION PLANS ------------------------------ */}
              {tab === "subscription-plans" && <BeginnerSubscriptionPlanManager />}

              {/* ------------------------------ PDF DOCUMENTS (Main / Practice) ------------------------------ */}
              {tab === "documents" && (
                <section className="beginner-admin-panel">
                  <div className="beginner-admin-panel-title">
                    <div>
                      <h2>Uploaded PDF Documents</h2>
                      <p>Manage Beginner question-bank PDFs and their imported questions.</p>
                    </div>
                    <button type="button" disabled={loadingPdfs || deletingPdfs} onClick={loadPdfDocuments}>
                      Refresh
                    </button>
                  </div>

                  <div className="beginner-doc-tabs" role="tablist" aria-label="PDF document type">
                    {PDF_SECTIONS.map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={pdfSection === key}
                        className={`beginner-doc-tab ${pdfSection === key ? "active" : ""}`}
                        onClick={() => setPdfSection(key)}
                      >
                        {label}
                        <span className="beginner-doc-tab-count">{pdfCounts[key]}</span>
                      </button>
                    ))}
                  </div>

                  <p className="beginner-admin-note">
                    Manage each PDF separately. If it is used by exam answers or saved attempts, deletion is blocked and
                    you can deactivate it instead.
                  </p>

                  {loadingPdfs ? (
                    <p>Loading PDF documents…</p>
                  ) : visiblePdfDocuments.length === 0 ? (
                    <p className="beginner-admin-empty">
                      No {pdfSection === "MAIN" ? "Main Exam" : "Practice"} PDF documents found.
                    </p>
                  ) : (
                    <div className="beginner-admin-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>{pdfSection === "MAIN" ? "Main Exam" : "Practice Set"}</th>
                            <th>Status</th>
                            <th>Questions</th>
                            <th>Uploaded</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePdfDocuments.map((document) => (
                            <tr key={document.id}>
                              <td>
                                <strong>{document.title || document.file_name}</strong>
                                <div>{document.file_name}</div>
                              </td>
                              <td>{document.target_name || "—"}</td>
                              <td>
                                {document.status} · {document.is_active ? "Active" : "Inactive"}
                                {document.error_message && (
                                  <div className="beginner-doc-error">{document.error_message}</div>
                                )}
                              </td>
                              <td>
                                {document.actual_questions ?? document.questions_created ?? 0}
                                {document.configured_question_count != null &&
                                  document.count_matches_configuration === false && (
                                    <div className="beginner-doc-error">
                                      Expected {document.configured_question_count}
                                    </div>
                                  )}
                              </td>
                              <td>{document.created_at ? new Date(document.created_at).toLocaleString() : "—"}</td>
                              <td>
                                <div className="beginner-admin-actions">
                                  <button
                                    type="button"
                                    className="secondary"
                                    onClick={() => setViewingPdfDocument(document)}
                                  >
                                    View
                                  </button>
                                  <button type="button" disabled={deletingPdfs} onClick={() => togglePdfActive(document)}>
                                    {document.is_active ? "Deactivate" : "Activate"}
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    disabled={deletingPdfs}
                                    onClick={() => deleteOnePdf(document)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Modal to view questions attached to a PDF Document */}
                  {viewingPdfDocument && (
                    <div className="beginner-admin-modal-backdrop" onClick={() => setViewingPdfDocument(null)}>
                      <section
                        className="beginner-admin-modal"
                        style={{ maxWidth: "860px", width: "90%", maxHeight: "85vh", overflowY: "auto" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="beginner-admin-panel-title">
                          <div>
                            <h2>Questions in {viewingPdfDocument.title || viewingPdfDocument.file_name}</h2>
                            <p>
                              Target: {viewingPdfDocument.target_name || viewingPdfDocument.target_type} ·{" "}
                              {viewingPdfDocument.questions?.length ?? 0} questions
                            </p>
                          </div>
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => setViewingPdfDocument(null)}
                          >
                            Close
                          </button>
                        </div>

                        {!viewingPdfDocument.questions || viewingPdfDocument.questions.length === 0 ? (
                          <p className="beginner-admin-empty">No imported questions found in this document.</p>
                        ) : (
                          <div className="beginner-pdf-questions-list" style={{ marginTop: "16px" }}>
                            {viewingPdfDocument.questions.map((q) => (
                              <div
                                key={q.id}
                                className="beginner-pdf-question-card"
                                style={{
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "8px",
                                  padding: "16px",
                                  marginBottom: "16px",
                                  backgroundColor: "#f8fafc",
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                  <strong>Question {q.question_number}</strong>
                                  <span style={{ fontSize: "0.85rem", color: "#64748b" }}>
                                    Type: {q.question_type} ({q.question_format}) · Marks: {q.marks}
                                  </span>
                                </div>
                                <p style={{ whiteSpace: "pre-wrap", marginBottom: "12px", color: "#1e293b" }}>
                                  {q.question_text}
                                </p>

                                {q.options && q.options.length > 0 && (
                                  <div style={{ marginLeft: "12px", marginBottom: "8px" }}>
                                    {q.options.map((opt) => (
                                      <div
                                        key={opt.id}
                                        style={{
                                          padding: "4px 8px",
                                          margin: "4px 0",
                                          borderRadius: "4px",
                                          backgroundColor: opt.is_correct ? "#dcfce7" : "#fff",
                                          border: opt.is_correct ? "1px solid #86efac" : "1px solid #cbd5e1",
                                          fontWeight: opt.is_correct ? "bold" : "normal",
                                        }}
                                      >
                                        {opt.label ? `${opt.label}. ` : ""}{opt.option_text}{" "}
                                        {opt.is_correct && <span style={{ color: "#16a34a" }}>(Correct Answer)</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {q.correct_answer && (!q.options || q.options.length === 0) && (
                                  <p style={{ fontSize: "0.9rem", color: "#16a34a" }}>
                                    <strong>Correct Answer:</strong> {q.correct_answer}
                                  </p>
                                )}

                                {q.explanation && (
                                  <p style={{ fontSize: "0.85rem", color: "#475569", marginTop: "8px", fontStyle: "italic" }}>
                                    <strong>Explanation:</strong> {q.explanation}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="beginner-admin-actions" style={{ marginTop: "20px" }}>
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => setViewingPdfDocument(null)}
                          >
                            Close
                          </button>
                        </div>
                      </section>
                    </div>
                  )}
                </section>
              )}

              {/* ------------------------------ RESULTS ------------------------------ */}
              {tab === "results" && (
                <section className="beginner-admin-panel">
                  <div className="beginner-admin-panel-title">
                    <div>
                      <h2>Beginner Results</h2>
                      <p>Review Beginner exam results and scores.</p>
                    </div>
                  </div>
                  <p className="beginner-admin-note">
                    The results screen is added, but live results need the matching Django results endpoint and API client
                    function. The current BeginnerAdmin API client does not expose a results endpoint, so this screen does
                    not invent or display fake student results.
                  </p>
                  <div className="beginner-admin-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Exam</th>
                          <th>Score</th>
                          <th>Result</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="6" className="beginner-admin-empty">
                            No results loaded. Connect the Beginner results API to display student attempts here.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* ------------------------------ SUBSCRIPTIONS ------------------------------ */}
              {tab === "subscriptions" && (
                <section className="beginner-admin-panel">
                  <h2>Beginner subscriptions ({subscriptions.length})</h2>
                  {subscriptions.length === 0 ? (
                    <p className="beginner-admin-empty">No subscription records yet.</p>
                  ) : (
                    table(
                      ["Student", "Plan", "Start", "Expiry", "Status", "Actions"],
                      subscriptions.map((x) => (
                        <tr key={x.id}>
                          <td>{x.student_email || x.student}</td>
                          <td>{x.name}</td>
                          <td>{x.starts_at ? new Date(x.starts_at).toLocaleDateString() : "—"}</td>
                          <td>{x.expires_at ? new Date(x.expires_at).toLocaleDateString() : "—"}</td>
                          <td>{x.status}</td>
                          <td>
                            <button type="button" onClick={() => setEditingSubscription(x)}>
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))
                    )
                  )}
                  {editingSubscription && (
                    <div className="beginner-admin-modal-backdrop">
                      <section className="beginner-admin-modal">
                        <h2>Manage subscription</h2>
                        <p>
                          {editingSubscription.student_email || `Student ${editingSubscription.student}`} ·{" "}
                          {editingSubscription.name}
                        </p>
                        <form className="beginner-admin-form" onSubmit={saveSubscription}>
                          <label>
                            Status
                            <select name="status" defaultValue={editingSubscription.status}>
                              <option value="PENDING">Pending</option>
                              <option value="ACTIVE">Active</option>
                              <option value="EXPIRED">Expired</option>
                              <option value="CANCELLED">Cancelled</option>
                            </select>
                          </label>
                          <label>
                            Admin notes
                            <textarea name="admin_notes" rows="4" defaultValue={editingSubscription.admin_notes || ""} />
                          </label>
                          <div className="beginner-admin-actions">
                            <button disabled={busy}>{busy ? "Saving…" : "Save subscription"}</button>
                            <button type="button" className="secondary" onClick={() => setEditingSubscription(null)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </section>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}